import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { changeLanguage } from 'services/i18n';
import { useAnalytics, usePageToken } from 'hooks';
import localStorageService from 'services/localStorage';
import { getRegion, isMobileSelector, isSdkSelector } from 'redux/selectors/global';
import OptionList from 'components/core/OptionList/OptionList';
import {
  CDN_IMAGES_URL,
  GA_ACTIONS,
  GA_CATEGORIES,
  LANGS,
  REGION_LANGS_MAPPER
} from 'consts';
import Drawer from 'components/core/Drawer/Drawer';
import { Elevation, FontWeights, Typographies } from 'models';
import { backDropShadow, Separator } from 'styles/global';
import Typography from 'components/core/Typography/Typography';

type LanguageKey = keyof typeof LANGS;

const sortLanguages = ({
  region,
  selectedLanguage,
  excludedLangs
}: {
  region: keyof typeof REGION_LANGS_MAPPER;
  selectedLanguage: string;
  excludedLangs: string[];
}) => {
  const sortFunc =
    Array.isArray(excludedLangs) && excludedLangs.length
      ? (langKey: string) =>
          selectedLanguage !== langKey && excludedLangs.indexOf(langKey) === -1
      : (langKey: string) => selectedLanguage !== langKey;

  const initialSort = Object.keys(LANGS).filter((lang: string) => sortFunc(lang));
  const regionArr = REGION_LANGS_MAPPER[region]?.filter(
    langKey => selectedLanguage !== langKey
  );
  const lexicalSortedArray = initialSort
    .filter(langKey => regionArr.indexOf(langKey) === -1)
    .sort();

  return [selectedLanguage, ...regionArr, ...lexicalSortedArray];
};

const Options = ({
  excludedLangs = [],
  selectedLanguage,
  isMenuOpen,
  closeMenu
}: {
  excludedLangs?: string[];
  selectedLanguage: string;
  isMenuOpen: boolean;
  closeMenu: () => void;
}) => {
  const {
    t,
    i18n: { language }
  } = useTranslation();
  const tracker = useAnalytics();

  const pageToken = usePageToken();
  const isMobile = useSelector(isMobileSelector);
  const isSdk = useSelector(isSdkSelector);
  const isMobileNotSdk = isMobile && !isSdk;
  const region = useSelector(getRegion);

  const saveLanguageInLocalStorage = (language: string) =>
    localStorageService.setItem(`${pageToken}_lang`, language);

  useEffect(() => {
    saveLanguageInLocalStorage(selectedLanguage);
  }, [selectedLanguage]);

  const listOptions = useMemo(() => {
    const langs = sortLanguages({
      region,
      selectedLanguage,
      excludedLangs
    });
    return langs.map((lang: string) => {
      const parsedFriendlyName = LANGS[lang as keyof typeof LANGS]?.friendlyName
        ? `(${LANGS[lang as LanguageKey]?.friendlyName})`
        : '';

      return {
        name: `language-item-${lang}`,
        value: lang,
        label: `${LANGS[lang as LanguageKey]?.lang} ${isMobile ? '' : parsedFriendlyName}`
      };
    });
  }, [JSON.stringify(excludedLangs), region, selectedLanguage]);

  const handleLanguageChange = (lang: string) => {
    tracker.fireEvent({
      category: `${GA_ACTIONS.SELECT} - ${GA_CATEGORIES.LANGUAGE_PICKER}`,
      action: `${GA_ACTIONS.SELECT} - ${GA_CATEGORIES.LANGUAGE_PICKER}`,
      label: lang
    });

    saveLanguageInLocalStorage(lang);
    changeLanguage(lang);
    closeMenu();
  };

  const LangMenu = (
    <LangMenuWrapper>
      <SelectLang type={Typographies.H7_HEADING}>{t('select_language')}</SelectLang>
      <StyledSeparator />
      <StyledOptionList
        options={listOptions}
        onChange={handleLanguageChange}
        value={language}
      />
    </LangMenuWrapper>
  );

  if (isMobileNotSdk) {
    return (
      <StyledDrawer open={isMenuOpen} placement="bottom" hideHeader showMask>
        {LangMenu}
      </StyledDrawer>
    );
  }

  return isMenuOpen ? LangMenu : null;
};

export default Options;

const StyledOptionList = styled(OptionList)`
  max-height: 70vh;
  width: 100%;
  box-shadow: unset;
  padding: 0 2.4rem;

  .option-list-option:not(:last-child) {
    border-bottom: 0.1rem solid ${({ theme }) => theme.colors.grey.grey5};
    border-radius: unset;
  }

  .option-list-option {
    padding: 1.6rem 0 1.6rem 0.8rem;
    margin: unset;
    ${({ value }) =>
      value &&
      css`
        &[data-testid$=${value}] {
          color: unset;
          background: unset;
          font-weight: ${FontWeights.SEMI_BOLD};
          position: relative;
        }
        &[data-testid$=${value}]::after {
          content: '';
          position: absolute;
          right: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 2.4rem;
          height: 2.4rem;
          background: url(${CDN_IMAGES_URL + '/selected-language.svg'}) no-repeat center
            center;
        }
      `}
  }
`;

const StyledDrawer = styled(Drawer)<{ open: boolean }>`
  [data-testid='drawer-header'] {
    display: none !important;
  }

  .drawer-content-wrapper,
  [data-testid='side_panel-content-wrapper'],
  [data-testid='drawer-content-wrapper'] {
    width: 100% !important;
    border-radius: 1.2rem 1.2rem 0 0;
  }

  [data-testid='option-list'] {
    overflow-x: hidden;
  }

  .drawer-mask,
  .drawer.drawer-open {
    ${({ open }) =>
      css`
        ${backDropShadow({ applyFullOpacity: open, transition: '0.1s' })}
      `};
  }
`;

const LangMenuWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: ${({ theme }) => theme.elevation[Elevation.DEPTH4]};
  border-radius: 1.2rem 1.2rem 0 0;
  background-color: ${({ theme }) => theme.colors.white};
  ${({ theme }) => {
    const { isMobileNotSdk } = theme.view;

    if (!isMobileNotSdk) {
      return css`
        width: 37.6rem;
        position: absolute;
        right: 0;
      `;
    }

    if (isMobileNotSdk) {
      return css`
        width: 100%;

        [data-testid^='language-item-'] {
          background: unset;
        }
      `;
    }
  }}
`;

const SelectLang = styled(Typography)`
  text-align: center;
  width: 100%;
  color: ${({ theme }) => theme.colors.secondaryText};
  margin-top: 2.4rem;
  margin-bottom: 2.4rem;
`;

const StyledSeparator = styled(Separator)`
  margin: unset;
  background: ${({ theme }) => theme.colors.grey.grey5};
`;
